<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuery } from '@tanstack/vue-query';
import { shiftsApi } from '@/api/shifts';
import { useGroupFilterStore } from '@/stores/group-filter';
import { createQueryParams } from '@/utils/query-params';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import ShiftsTodayMap from './shifts-today-map.vue';

// Same cadence as the ActiveAdmin dashboard map this view mirrors.
const REFRESH_INTERVAL_MS = 60_000;

const { groupIds } = storeToRefs(useGroupFilterStore());

function summaryQuery(startDateTimeFilter: 'today' | 'active_now') {
  const params = computed(() => createQueryParams({
    startDateTimeFilter,
    offset: 0,
    patientGroupsIdIn: groupIds.value || [],
    light: 'true',
  }));

  return useQuery({
    queryKey: computed(() => ['todayShiftsSummary', startDateTimeFilter, params.value]),
    queryFn: () => shiftsApi.indexTodayList(params.value),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

// Today's shifts (those starting today) feed the totals; the shifts under way
// now come from their own query, because a night shift that started yesterday
// is active this morning without being one of today's.
const todayQuery = summaryQuery('today');
const activeQuery = summaryQuery('active_now');

const todayShifts = computed(() => todayQuery.data.value ?? []);
const activeShifts = computed(() => activeQuery.data.value ?? []);
const isPending = computed(() => todayQuery.isPending.value || activeQuery.isPending.value);
const isError = computed(() => todayQuery.isError.value || activeQuery.isError.value);

const stats = computed(() => [
  { label: 'Turnos totales hoy', value: todayShifts.value.length },
  { label: 'Turnos activos ahora', value: activeShifts.value.length },
  {
    label: 'Turnos con entrada marcada',
    value: todayShifts.value.filter(shift => !!shift.checkIn).length,
  },
]);

const todayText = computed(() => {
  const text = new Date().toLocaleDateString('es-CL', { dateStyle: 'full' });

  return text.charAt(0).toUpperCase() + text.slice(1);
});

const updatedAtText = computed(() => (activeQuery.dataUpdatedAt.value ?
  new Date(activeQuery.dataUpdatedAt.value).toLocaleTimeString('es-CL', {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }) :
  null));
</script>
<template>
  <v-card class="w-100">
    <v-toolbar
      color="white"
      flat
    >
      <v-toolbar-title class="font-weight-bold">
        Resumen de hoy
      </v-toolbar-title>
    </v-toolbar>
    <v-card-text class="pt-0">
      <div class="d-flex flex-wrap align-center ga-2 mb-4">
        <p class="text-body-2 text-medium-emphasis">
          {{ todayText }}
          <template v-if="updatedAtText">
            · Actualizado a las {{ updatedAtText }}
          </template>
        </p>
        <v-spacer />
        <group-filter-select
          v-model="groupIds"
          hide-details
          density="compact"
          class="today-summary__groups"
        />
      </div>
      <v-alert
        v-if="isError"
        type="error"
        variant="tonal"
        class="mb-4"
      >
        No se pudo cargar el resumen de hoy.
      </v-alert>
      <v-row class="mb-2">
        <v-col
          v-for="stat in stats"
          :key="stat.label"
          cols="12"
          sm="4"
        >
          <v-card
            elevation="2"
            class="h-100"
          >
            <v-card-text>
              <v-skeleton-loader
                v-if="isPending"
                type="heading"
              />
              <p
                v-else
                class="text-h4 font-weight-bold"
              >
                {{ stat.value }}
              </p>
              <p class="text-body-2 text-medium-emphasis">
                {{ stat.label }}
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <shifts-today-map
        :shifts="activeShifts"
        :loading="isPending"
      />
    </v-card-text>
  </v-card>
</template>
<style scoped>
.today-summary__groups {
  max-width: 280px;
}
</style>
